<template>
  <main>
    <form class="container" @submit.prevent="submit">
      <div class="row">
        <div class="col-full">
          <h1 class="heading-xl">
            {{ $t('enrollTerms.heading') }}
          </h1>
        </div>
      </div>

      <al-notify v-if="errorToken" error name="error-msg">
        {{ $t(errorToken) }}
      </al-notify>

      <register-form v-if="showRegisterForm" v-model="form" />

      <div v-if="nationalIdConfig" class="row">
        <div class="col-full">
          <h2 class="heading-md">
            {{ $t('common.nationalId') }}
          </h2>
          <al-input
            v-model="nationalId"
            v-validate="nationalIdValidationRules"
            :mask="nationalIdConfig.mask"
            :label="$t('common.identificationNumber')"
            :manual-error="nationalIdManualError"
            name="national-id"
            @input="nationalIdManualError = null" />
        </div>
      </div>

      <div class="row">
        <h2 class="col-full heading-md">{{ $t('enrollTerms.marketingHeading') }}</h2>
      </div>

      <div class="row">
        <div class="col-full">
          <al-checkbox
            v-model="marketingOptIn"
            :label="$t('common.marketingOptIn')"
            :subtext="$t('common.marketingOptInSubtext')"
            name="marketingOptIn"
            data-test="checkbox-marketing-opt-in" />
        </div>
      </div>

      <tux-terms-consent
        :button-text="$t(buttonText)"
        :terms-url="termsUrl"
        :privacy-url="privacyUrl"
        flex-grow
        name="enroll" />

      <div class="row row-vertical-align-bottom">
        <div class="col-full align-center">
          <button
            :disabled="hasValidationErrors || nationalIdManualError"
            data-test="form-submit-button"
            class="fill uppercase"
            type="submit"
            :aria-label="$t(buttonText)">
            {{ $t(buttonText) }}
          </button>
        </div>
        <div class="col-full align-center">
          <back-button v-analytics:click="$EVENTS.CANCEL_BUTTON" :location="cancelBtnLocation">
            {{ $t('button.cancel') }}
          </back-button>
        </div>
      </div>
    </form>
  </main>
</template>

<script>
import { AlCheckbox, AlInput, AlNotify } from '@mc-alberta/alcapone'
import nudetect from '@mc-alberta/nudetect'
import { TuxTermsConsent } from '@mc-alberta/tuxedo'
import { getCurrentInstance } from '@vue/composition-api'
import { mapActions, mapGetters, mapState } from 'vuex'
import BackButton from '@/components/BackButton'
import RegisterForm from '@/components/RegisterForm'
import useBindUserData from '@/composables/useBindUserData'
import useConfirmModal from '@/composables/useConfirmModal'
import useFormValidation from '@/composables/useFormValidation'
import CONSTANTS from '@/constants'

export default {
  name: CONSTANTS.ROUTES.REGISTER_TERMS,
  components: { AlCheckbox, RegisterForm, AlInput, AlNotify, BackButton, TuxTermsConsent },
  beforeRouteLeave(to, from, next) {
    this.beforeRouteLeaveConfirmModal(to, from, next)
  },
  setup(props, context) {
    const { proxy } = getCurrentInstance()

    const { errorToken, handleBind, nationalIdManualError } = useBindUserData()
    const { beforeRouteLeaveConfirmModal } = useConfirmModal({
      context,
      forceConfirmationModal: true,
      onConfirmModalComplete: () => proxy.$store.dispatch('clearNewCardData')
    })
    const { hasValidationErrors } = useFormValidation()

    return {
      beforeRouteLeaveConfirmModal,
      errorToken,
      handleBind,
      hasValidationErrors,
      nationalIdManualError
    }
  },
  data() {
    return {
      card: null,
      countryOptions: this.$i18nData.getPhonesConfig(),
      form: {
        email: '',
        phone: {
          country: '',
          countryCode: '',
          phoneNumber: ''
        }
      },
      initialPhoneNumber: this.$store.state.registrationData?.consumer?.mobileNumber,
      initialEmailAddress: this.$store.state.registrationData?.consumer?.emailAddress,
      marketingOptIn: false,
      storedPayload: null,
      buttonText: 'button.continue',
      cancelBtnLocation: { name: CONSTANTS.ROUTES.ENTRY, params: { logout: true } },
      nationalId: ''
    }
  },
  computed: {
    ...mapState({
      newCard: (state) => state.cards.newCard,
      countryCode: (state) => state.user.consumer.countryCode,
      consumer: (state) => state.user.consumer,
      registrationData: (state) => state.registrationData,
      consumerIdentity: (state) => state.identity.consumerIdentity
    }),
    ...mapGetters([
      'privacyUrl',
      'termsUrl',
      'newCardAddressFormat',
      'complianceTermsUrl',
      'compliancePrivacyUrl'
    ]),
    nationalIdConfig() {
      const { consumer } = this.$i18nData.getCountryConfig(this.countryCode)
      return consumer?.nationalId
    },
    nationalIdValidationRules() {
      const config = this.nationalIdConfig
      const { required, minimumLength: min, maximumLength: max } = config || {}
      return { required, min, max, national_id: true }
    },
    showRegisterForm() {
      return !this.initialPhoneNumber || !this.initialEmailAddress
    }
  },
  watch: {
    countryCode: function (countryCode) {
      if (this.fields.phoneNumber && !this.fields.phoneNumber.dirty) {
        this.form.phone.country = countryCode
      }
    }
  },
  created() {
    if (!this.newCard) {
      return this.$router.replace({
        name: CONSTANTS.ROUTES.LIST_CARDS,
        params: { skipConfirmation: true }
      })
    }

    const { emailAddress, mobileNumber } = this.registrationData?.consumer || {}
    this.form.email = emailAddress || this.consumer.email || this.form.email
    this.form.phone = mobileNumber || this.form.phone

    if (!this.form.phone.countryCode) {
      this.form.phone.country = this.$store.state.user.consumer.countryCode
    }

    // setting it here to avoid vue reactivity updating the value ~ Ronald
    this.card = { ...this.newCard }
  },
  mounted() {
    this.$nextTick(() => nudetect.beginBehaviouralMonitoring())
    this.$validator.validateAll()
  },
  beforeDestroy() {
    this.clearServiceLayerError()
  },
  methods: {
    ...mapActions([
      'clearNewCardData',
      'clearServiceLayerError',
      'setRegistrationData',
      'startLoader'
    ]),

    async submit() {
      this.startLoader(CONSTANTS.LOADERS.ADD_PAYMENT)
      this.$analytics.track({
        event: this.$EVENTS.CONTINUE_BUTTON,
        meta: {
          marketingOptIn: this.marketingOptIn,
          hasBillingData: CONSTANTS.ADDRESS_FORMAT.NONE,
          hasShippingData: false,
          hasNationalId: !!this.nationalIdConfig,
          marketBillingPreference: this.newCardAddressFormat,
          consumerNameRequested: false
        }
      })

      this.buildPayload()
      await this.handleBind(this.storedPayload)
    },

    buildPayload() {
      const { languageCode, countryCode } = this.consumer

      this.setRegistrationData({
        consumer: {
          emailAddress: this.form.email,
          mobileNumber: this.form.phone
        }
      })
      delete this.registrationData.consumer.mobileNumber.country

      const consumer = {
        ...this.registrationData.consumer,
        consumerIdentity: this.consumerIdentity,
        // passing 'latest' informs backend that user is accepting latest policy version ~ Kerry
        complianceSettings: {
          privacyAcceptedVersion: 'latest',
          privacyLatestVersionUri: this.compliancePrivacyUrl,
          tncAcceptedVersion: 'latest',
          tncLatestVersionUri: this.complianceTermsUrl,
          cookieAcceptedVersion: '1.0',
          marketingOptIn: this.marketingOptIn
        },
        countryCode,
        languageCode
      }
      /**
       * Send national ID based on i18n data
       */
      if (this.nationalIdConfig) {
        consumer.nationalIdentifier = this.nationalId
      }

      this.storedPayload = {
        consumer
      }
    }
  }
}
</script>

<style scoped>
.row-vertical-align-bottom {
  flex-grow: 0;
}
</style>
